import {
  formatFileSize,
  getFileTypeIcon,
} from '@/components/common/FileUpload/fileUtils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { Download, FileText, Loader2, Trash2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';

/**
 * Generic file interface for the FileUploadList component
 * This interface defines the minimum required properties for any file to be displayed in the list
 */
export interface UploadableFile {
  /** Unique identifier for the file */
  id: string | number;
  /** Original filename as uploaded by the user */
  original_filename: string;
  /** MIME type of the file */
  mime_type: string;
  /** File size in bytes */
  file_size: number;
}

interface FileUploadListProps<T extends UploadableFile> {
  files: T[];
  isLoading?: boolean;
  onDownload?: (file: T) => void;
  onDelete?: (file: T) => void;
  isDownloading?: boolean;
  isDeleting?: boolean;
  showActions?: boolean;
  showActionsLabel?: boolean;
  actionsVariant?:
    'ghost' | 'outline' | 'default' | 'destructive' | 'secondary' | 'link';
  className?: string;
  emptyMessage?: string | null;
}

export const FileUploadList = <T extends UploadableFile>({
  files,
  isLoading = false,
  onDownload,
  onDelete,
  isDownloading = false,
  isDeleting = false,
  showActions = true,
  showActionsLabel = true,
  actionsVariant = 'outline',
  className = '',
  emptyMessage,
}: FileUploadListProps<T>) => {
  const { t } = useTranslation();

  /**
   * Get the appropriate Lucide icon component based on file MIME type or file extension
   * Memoized for performance optimization
   */
  const getFileIcon = useCallback((file: T) => {
    const mimeType = file.mime_type?.toLowerCase() || '';
    const fileName = file.original_filename?.toLowerCase() || '';

    return getFileTypeIcon(mimeType, fileName);
  }, []);

  /**
   * Memoized handlers to prevent unnecessary re-renders
   */
  const handleDownloadFile = useCallback(
    (file: T) => onDownload?.(file),
    [onDownload]
  );

  const handleDeleteFile = useCallback(
    (file: T) => onDelete?.(file),
    [onDelete]
  );

  /**
   * Memoize the empty state to prevent unnecessary re-renders
   * Only render if emptyMessage is not explicitly empty or null
   */
  const emptyState = useMemo(() => {
    if (emptyMessage === '' || emptyMessage === null) return null;

    return (
      <div className='text-muted-foreground flex items-center justify-start gap-1'>
        <FileText className='h-8 w-8 opacity-50' />
        <p>{emptyMessage || t('common.fileManagement.noFiles')}</p>
      </div>
    );
  }, [emptyMessage, t]);

  // Memoize the loading state
  const loadingState = useMemo(
    () => (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-6 w-6 animate-spin' />
      </div>
    ),
    []
  );

  return (
    <div className={cn('space-y-3', className)}>
      {isLoading ? (
        loadingState
      ) : files.length === 0 ? (
        emptyState
      ) : (
        <div className='grid gap-3 w-full'>
          {files.map((file, index) => {
            const FileIcon = getFileIcon(file);

            return (
              <div
                key={`uploaded-${file.id}-${index}-${file.original_filename}`}
                className='flex items-center justify-between p-3 border rounded-lg w-full overflow-hidden'
              >
                <div className='flex items-center space-x-3 min-w-0 flex-1 overflow-hidden'>
                  <FileIcon className='h-5 w-5 text-muted-foreground flex-shrink-0' />
                  <div className='min-w-0 flex-1 overflow-hidden'>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className='text-sm font-medium truncate max-w-full cursor-help'>
                            {file.original_filename ||
                              t('common.fileManagement.unknownFile')}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className='max-w-xs break-words'>
                            {file.original_filename ||
                              t('common.fileManagement.unknownFile')}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <p className='text-xs text-muted-foreground truncate'>
                      {file.mime_type || t('common.fileManagement.unknownType')}{' '}
                      • {formatFileSize(file.file_size || 0, t)}
                    </p>
                  </div>
                </div>

                {showActions && (
                  <div className='flex items-center space-x-2'>
                    {onDownload && (
                      <Button
                        variant={actionsVariant}
                        size='sm'
                        type='button'
                        onClick={() => handleDownloadFile(file)}
                        disabled={isDownloading}
                        className='gap-1'
                      >
                        <Download className='h-4 w-4' />
                        {showActionsLabel && (
                          <> {t('common.fileManagement.downloadFile')}</>
                        )}
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        variant={actionsVariant}
                        size='sm'
                        type='button'
                        onClick={() => handleDeleteFile(file)}
                        disabled={isDeleting}
                        className='gap-1 text-red-600 hover:text-red-700'
                      >
                        <Trash2 className='h-4 w-4' />
                        {showActionsLabel && (
                          <> {t('common.fileManagement.deleteFile')}</>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Add display name for better debugging
FileUploadList.displayName = 'FileUploadList';

export default FileUploadList;
